import { Brand, Campaign, LineItemGroup, Market } from '../types/quotation'

export const quotationMeta = {
  client: 'Leo Pharma',
  title: 'Snakker × Leo Pharma',
  subtitle: 'Interactive quotation for 3 corporate brands, EU5 deployment',
  reference: 'SNK-LEO-2026-01',
  validUntil: '30 September 2026',
  preparedOn: '10 August 2026'
}

export const brands: Brand[] = [
  {
    id: 'brand-1',
    name: 'Anzupgo',
    therapyArea: 'Chronic hand eczema',
    note: 'Snaks embedded on the existing complex site as a quick win while the longer-term redesign runs in parallel.'
  },
  {
    id: 'brand-2',
    name: 'Adtralza',
    therapyArea: 'Atopic dermatitis',
    note: 'Website snakification plus the shared master campaign set across EU5.'
  },
  {
    id: 'brand-3',
    name: 'Spevigo',
    therapyArea: 'Generalised pustular psoriasis',
    note: 'Website snakification plus the shared master campaign set across EU5.'
  }
]

export const markets: Market[] = [
  { id: 'de', name: 'Germany', code: 'DE', deadline: 'October deadline' },
  { id: 'es', name: 'Spain', code: 'ES', deadline: 'October deadline' },
  { id: 'fr', name: 'France', code: 'FR', deadline: 'October deadline' },
  { id: 'it', name: 'Italy', code: 'IT', deadline: 'October deadline' },
  {
    id: 'uk',
    name: 'United Kingdom',
    code: 'UK',
    deadline: 'October deadline'
  }
]

export const campaigns: Campaign[] = [
  {
    id: 'c1',
    name: 'Hook & Teaser: drive traffic to the Snakified site',
    description:
      'A Hook and a Teaser snak with a website CTA, engineered to pull HCPs from email, banner and rep channels into the newly snakified brand site.',
    status: 'Defined'
  },
  {
    id: 'c2',
    name: 'Master Snak campaign 02',
    description:
      'Use case to be agreed. Candidates include a mechanism-of-action explainer, a guideline update or a patient-case snak.',
    status: 'To be agreed'
  },
  {
    id: 'c3',
    name: 'Master Snak campaign 03',
    description:
      'Use case to be agreed. Candidates include congress highlights or a dosing and administration refresher.',
    status: 'To be agreed'
  },
  {
    id: 'c4',
    name: 'Master Snak campaign 04',
    description:
      'Use case to be agreed. Candidates include a real-world evidence digest or patient-support programme awareness.',
    status: 'To be agreed'
  },
  {
    id: 'c5',
    name: 'Master Snak campaign 05',
    description:
      'Use case to be agreed. Candidates include a returning-visitor re-engagement snak informed by early campaign data.',
    status: 'To be agreed'
  }
]

export const lineItemGroups: LineItemGroup[] = [
  {
    id: 'campaigns',
    title: 'Global brand & country campaigns',
    subtitle:
      'Snakification of existing content, website and campaign localization into EU5, master campaign production and in-market activation.',
    items: [
      {
        id: 'snakification',
        name: 'Snakification of website content',
        description:
          'Conversion of each global brand’s existing long-form website content into short-form, interactive snaks that sit on top of the SEO repository.',
        unitPrice: 22000,
        unitLabel: 'per brand',
        quantity: 3,
        quantityLabel: '3 global brands',
        cadence: 'one-off',
        includes: [
          'Content audit and snakification strategy per brand',
          'Interactive snak build for core website journeys',
          'Embed layer for the existing brand site, SEO content untouched',
          'MLR-ready export pack for medical and regulatory review'
        ]
      },
      {
        id: 'website-localization',
        name: 'Website snakification localization',
        description:
          'Localization of each brand’s snakified website content into each of the five EU5 markets, so the on-site snak layer runs in local language from day one.',
        unitPrice: 3000,
        unitLabel: 'per brand, per country',
        quantity: 15,
        quantityLabel: '3 brands × 5 countries',
        cadence: 'one-off',
        includes: [
          'Translation and cultural adaptation of the on-site snak layer',
          'Local claim, reference and indication alignment',
          'Local MLR support pack per market',
          'Deployment onto each local brand site'
        ]
      },
      {
        id: 'master-campaigns',
        name: 'Master Snak campaigns',
        description:
          'Master campaigns designed to drive engagement with the snakified content. Campaign 01 is the Hook & Teaser with a website CTA; four further use cases to be agreed.',
        unitPrice: 3000,
        unitLabel: 'per master campaign',
        quantity: 5,
        quantityLabel: '5 master campaigns',
        cadence: 'one-off',
        includes: [
          'Campaign concept, narrative and engagement mechanic',
          'Hook and Teaser assets with website CTA',
          'Master language build, ready for localization',
          'Measurement plan and success criteria'
        ]
      },
      {
        id: 'localization',
        name: 'Campaign localization per market',
        description:
          'Adaptation of each master campaign and its snakified content into each EU5 market: language, local claims, local references and local CTA.',
        unitPrice: 1000,
        unitLabel: 'per campaign, per country',
        quantity: 25,
        quantityLabel: '5 campaigns × 5 countries',
        cadence: 'one-off',
        includes: [
          'Translation and cultural adaptation',
          'Local claim and reference alignment',
          'Local MLR support pack',
          'Market-specific CTA and routing'
        ]
      },
      {
        id: 'activation',
        name: 'Country activation & campaign insights',
        description:
          'In-market deployment, optimisation and reporting for twelve months per campaign, per country.',
        unitPrice: 99,
        unitLabel: 'per country, per campaign, per month',
        quantity: 300,
        quantityLabel: '5 countries × 5 campaigns × 12 months',
        cadence: 'monthly',
        includes: [
          'Deployment and channel routing in market',
          'Live engagement monitoring and optimisation',
          'Qualitative and quantitative HCP engagement data',
          'Monthly insight readout to global and local teams'
        ]
      }
    ]
  },
  {
    id: 'platform',
    title: 'Control Tower platform',
    subtitle:
      'Central visibility, governance and deployment control across every brand, campaign and market.',
    items: [
      {
        id: 'control-tower',
        name: 'Control Tower platform licence',
        description:
          'Central overview of all global and local campaign performance metrics, with centralised control of access and deployment.',
        unitPrice: 50000,
        unitLabel: 'per year',
        quantity: 1,
        quantityLabel: '3 brands, 5 countries',
        cadence: 'annual',
        includes: [
          'Global and local performance dashboards',
          'Campaign calendar and deployment control',
          'Role-based access for global, regional and local teams',
          'Benchmarks across brands, markets and campaign types'
        ]
      },
      {
        id: 'central-hub',
        name: 'Adaptation to Leo Pharma Central Hub',
        description:
          'One-off engineering to align Control Tower with Leo Pharma Central Hub requirements, with a path to Snowflake Nucleus integration.',
        unitPrice: 20000,
        unitLabel: 'once-off',
        quantity: 1,
        quantityLabel: 'One-off integration',
        cadence: 'one-off',
        includes: [
          'Requirements alignment with Leo Pharma data and IT',
          'Data model and taxonomy mapping',
          'Secure data feed into the Central Hub',
          'Documentation and handover'
        ]
      }
    ]
  }
]

export const opportunityStats = [
  {
    value: '30–35%',
    label: 'of big pharma revenue comes from legacy and established products'
  },
  {
    value: '76–85%',
    label: 'of Leo Pharma revenue sits in established and core brands'
  },
  {
    value: '<1 week',
    label: 'campaign turnaround once the snakified layer is live'
  },
  {
    value: '5 EU5',
    label: 'markets running the same campaigns from one master build'
  }
]

export const solutionPillars = [
  {
    title: 'Snakify what you already own',
    body: 'No new content. We convert complex, lengthy materials into short-form, interactive snaks that drive HCP engagement and data collection.'
  },
  {
    title: 'A website enhancement layer',
    body: 'Snaks embed directly into complex brand sites such as Anzupgo, while SEO-optimised long-form stays the repository. A quick win while redesigns run in parallel.'
  },
  {
    title: 'A campaign engine',
    body: 'We run the calendar and planning, localization and adaptation, execution and deployment, and measurement and optimisation.'
  },
  {
    title: 'A central hub view',
    body: 'Control Tower gives Leo Pharma analytics across every campaign and market, with a path to integration with the Snowflake Nucleus platform.'
  }
]

export const phases = [
  {
    tag: 'Phase 1 · This quotation',
    title: 'Snak campaigns in the brand strategy by October',
    body: 'Three strategic corporate brands, snakified and activated across the top EU5 markets, with snak campaigns built into the brand plans rather than run alongside them. This establishes the governance, processes and ways of working that everything after it runs on.',
    active: true
  },
  {
    tag: 'Phase 2 · Geographic expansion',
    title: 'Beyond EU5',
    body: 'The same three brands and the same master campaigns extended into additional markets. The master build is already paid for, so each new country only adds localization and activation.',
    active: false
  },
  {
    tag: 'Phase 3 · 2027 budget cycle',
    title: 'Core and established brands',
    body: 'The 85% of revenue sitting in core and established brands is planned into the 2027 budget cycle, so funding is committed at portfolio level rather than found brand by brand.',
    active: false
  }
]

export const nextSteps = [
  {
    title: 'Kick off on Anzupgo, Adtralza and Spevigo',
    body: 'Confirm site access and content inventory for the three brands, starting with the most complex site.',
    timing: 'Week 1'
  },
  {
    title: 'Snakification sprint',
    body: 'Content audit and snakification of the three corporate brand sites, with governance and HCP engagement approvals agreed in parallel.',
    timing: 'Weeks 2–6'
  },
  {
    title: 'Hook & Teaser go-live',
    body: 'First master campaign built and localized into all five EU5 markets ahead of the October deadline.',
    timing: 'Weeks 6–10'
  },
  {
    title: 'Run, measure, expand',
    body: 'Remaining campaigns released on the calendar across a full twelve months of activation, Control Tower reporting on all five markets, and a costed case for additional geographies.',
    timing: 'Months 3–12'
  }
]
